import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

/**
 * `?task=KEY` — every open task has a URL (docs/release-1/17-V2-BUILD-PLAN.md
 * §5 W4-0; used by My Work, list, kanban, the mobile sheet stack).
 *
 *  - `open('WEB-45')` PUSHES a history entry (`replace:false`) on the current
 *    page, so Safari / Android Back closes the task instead of leaving the page.
 *  - `close()` steps Back when this tab pushed that entry (so the history
 *    doesn't grow a "reopen" step); for a task that arrived in the URL
 *    (shared link, reload of a cold PWA, a link from elsewhere) it removes the
 *    param in place instead (`replace:true`) and stays on the page.
 *  - Other search params of the page are kept as they are.
 *
 * The key is normalized to upper case and must look like a task key
 * (`WEB-45`); anything else reads as "no task".
 */
export interface TaskUrl {
  /** The open task's key, or null. */
  taskKey: string | null;
  open(key: string): void;
  close(): void;
}

/** Search param name. */
export const TASK_URL_PARAM = 'task';

const TASK_KEY_RE = /^[A-Z][A-Z0-9]{0,9}-\d{1,9}$/;
/** History-state field marking an entry `open()` pushed in THIS page load. */
const STATE_FIELD = 'projointTaskUrl';
const PAGE_LOAD = `${Date.now().toString(36)}.${Math.random().toString(36).slice(2, 8)}`;

/** A valid task key from a raw search value (`'web-45'` → `'WEB-45'`), else null. */
export function parseTaskKey(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const key = raw.trim().toUpperCase();
  return TASK_KEY_RE.test(key) ? key : null;
}

type Search = Record<string, unknown>;

function taskOf(search: unknown): string | null {
  return parseTaskKey((search as Search | undefined)?.[TASK_URL_PARAM]);
}

export function useTaskUrl(): TaskUrl {
  const router = useRouter();
  const navigate = useNavigate();
  const taskKey = useRouterState({ select: (s) => taskOf(s.location.search) });

  const open = useCallback(
    (key: string) => {
      const next = parseTaskKey(key);
      if (!next || next === taskOf(router.state.location.search)) return;
      const mark = `${PAGE_LOAD}:${next}`;
      void navigate({
        search: ((prev: Search) => ({ ...prev, [TASK_URL_PARAM]: next })) as never,
        state: ((prev: Search) => ({ ...prev, [STATE_FIELD]: mark })) as never,
        replace: false,
      });
    },
    [navigate, router],
  );

  const close = useCallback(() => {
    const loc = router.state.location;
    const current = taskOf(loc.search);
    if (!current) return;
    const pushedHere =
      (loc.state as unknown as Search | undefined)?.[STATE_FIELD] === `${PAGE_LOAD}:${current}`;
    if (pushedHere && router.history.canGoBack()) {
      router.history.back();
      return;
    }
    void navigate({
      search: ((prev: Search) => {
        const rest = { ...prev };
        delete rest[TASK_URL_PARAM];
        return rest;
      }) as never,
      replace: true,
    });
  }, [navigate, router]);

  return useMemo(() => ({ taskKey, open, close }), [taskKey, open, close]);
}
